import clsx from 'clsx';
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
import { validType } from './util';
import { getZoomIconDefaultStyles } from '../../Shared/zoomIcon';
const componentName = 'Button';
const prefixName = `ui-${componentName}`;
export const buttonClasses = {
    root: `${prefixName}-root-${randomStr()}`,
    focusVisible: `${prefixName}-focusVisible`,
    extraSmall: `${prefixName}-extraSmall`,
    small: `${prefixName}-small`,
    medium: `${prefixName}-medium`,
    large: `${prefixName}-large`,
    extraLarge: `${prefixName}-extraLarge`,
    primary: `${prefixName}-primary`,
    secondary: `${prefixName}-secondary`,
    tertiary: `${prefixName}-tertiary`,
    overlay: `${prefixName}-overlay`,
    loading: `${prefixName}-loading`,
    disabled: `${prefixName}-disabled`,
    danger: `${prefixName}-danger`,
    startIcon: `${prefixName}-startIcon`,
    endIcon: `${prefixName}-endIcon`,
    loadingIcon: `${prefixName}-loadingIcon`,
    loadingMask: `${prefixName}-loadingMask`,
    iconButton: `${prefixName}-iconButton`,
    homeButton: `${prefixName}-homeButton`,
};
export function getStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName: 'Button',
        theme,
        dynamicToken,
    });
    const getSizeCSS = (btnSize) => {
        const size = btnSize;
        return {
            height: tk(token.height[size]),
            minWidth: tk(token.minWidth[size]),
            padding: tk(token.padding[size]),
            fontSize: tk(token.fontSize[size]),
            fontWeight: tk(token.fontWeight[size]),
            borderRadius: tk(token.borderRadius[size]),
            lineHeight: tk(token.lineHeight[size]),
            // icon
            [`.${buttonClasses.startIcon}, .${buttonClasses.endIcon}`]: {
                fontSize: tk(token.fontSize[size]),
                svg: {
                    fontSize: tk(token.fontSize[size]),
                },
            },
            // loading mask
            [`.${buttonClasses.loadingMask}`]: {
                borderRadius: tk(token.borderRadius[size]),
            },
        };
    };
    const getTypeCSS = (btnType) => {
        const type = btnType;
        return {
            background: tk(token.background[type].default),
            color: tk(token.color[type].default),
            borderColor: tk(token.borderColor[type].default),
            '&:hover': {
                background: tk(token.background[type].hover),
                color: tk(token.color[type].hover),
                borderColor: tk(token.borderColor[type].hover),
            },
            '&:active': {
                background: tk(token.background[type].active),
                color: tk(token.color[type].active),
                borderColor: tk(token.borderColor[type].active),
            },
            // loading mask offset
            [`.${buttonClasses.loadingMask}`]: {
                left: tk(token.loadingMaskOffset[type]),
                right: tk(token.loadingMaskOffset[type]),
                top: tk(token.loadingMaskOffset[type]),
                bottom: tk(token.loadingMaskOffset[type]),
            },
        };
    };
    const getDangerCSS = (btnType) => {
        const type = btnType && validType(btnType) ? btnType : 'default';
        const dangerCSS = {
            background: tk(token.background.danger[type].default),
            color: tk(token.color.danger[type].default),
            borderColor: tk(token.borderColor.danger[type].default),
            // fontWeight: tk(token.fontWeight[type]),
            '&:hover': {
                background: tk(token.background.danger[type].hover),
                color: tk(token.color.danger[type].hover),
                borderColor: tk(token.borderColor.danger[type].hover),
            },
            '&:active': {
                background: tk(token.background.danger[type].active),
                color: tk(token.color.danger[type].active),
                borderColor: tk(token.borderColor.danger[type].active),
            },
        };
        return dangerCSS;
    };
    const getLoadingCSS = (btnType) => {
        const type = btnType && validType(btnType) ? btnType : 'default';
        // Revert to default style even if hover or active for loading buttons
        // Can be removed and replace the hover / active state with :hover:not(.loading) to simplify the structure when :not() is widely supported
        const loadingCSS = {
            '&, &:hover, &:active': {
                background: tk(token.background[type].default),
                color: tk(token.color[type].default),
                borderColor: tk(token.borderColor[type].default),
            },
            [`&.${buttonClasses.danger}`]: {
                '&, &:hover, &:active': {
                    background: tk(token.background.danger[type].default),
                    color: tk(token.color.danger[type].default),
                    borderColor: tk(token.borderColor.danger[type].default),
                },
            },
        };
        return loadingCSS;
    };
    const getDisabledCSS = (btnType) => {
        const type = btnType && validType(btnType) ? btnType : 'default';
        const disabledCSS = {
            background: tk(token.background.disabled[type].default),
            color: tk(token.color.disabled[type].default),
            borderColor: tk(token.borderColor.disabled[type].default),
            // fontWeight: tk(token.fontWeight[type]),
            '&:hover': {
                background: tk(token.background.disabled[type].hover),
                color: tk(token.color.disabled[type].hover),
                borderColor: tk(token.borderColor.disabled[type].hover),
            },
            '&:active': {
                background: tk(token.background.disabled[type].active),
                color: tk(token.color.disabled[type].active),
                borderColor: tk(token.borderColor.disabled[type].active),
            },
        };
        return disabledCSS;
    };
    const newRootStyle = {
        [`&.${buttonClasses.root}`]: {
            fontFamily: tk(token.fontFamily),
            textTransform: 'none',
            // boxShadow: 'none',
            borderStyle: 'solid',
            borderWidth: 1,
            whiteSpace: 'nowrap',
            position: 'relative',
            // box style
            height: tk(token.height.default),
            minWidth: tk(token.minWidth.default),
            padding: tk(token.padding.default),
            borderRadius: tk(token.borderRadius.default),
            lineHeight: tk(token.lineHeight.default),
            // text style
            fontSize: tk(token.fontSize.default),
            fontWeight: tk(token.fontWeight.default),
            // color style
            background: tk(token.background.default.default),
            color: tk(token.color.default.default),
            borderColor: tk(token.borderColor.default.default),
            '&:hover': {
                background: tk(token.background.default.hover),
                color: tk(token.color.default.hover),
                borderColor: tk(token.borderColor.default.hover),
            },
            '&:active': {
                background: tk(token.background.default.active),
                color: tk(token.color.default.active),
                borderColor: tk(token.borderColor.default.active),
            },
            [`&.${buttonClasses.focusVisible}, &:focus-visible`]: {
                outline: `2px solid ${tk(token.focusBorderColor)}`,
                outlineOffset: '2px',
            },
            // icon
            [`.${buttonClasses.startIcon}, .${buttonClasses.endIcon}`]: {
                fontSize: tk(token.fontSize.default),
                svg: {
                    fontSize: tk(token.fontSize.default),
                },
            },
            [`.${buttonClasses.startIcon}`]: {
                marginInlineStart: 0,
                marginInlineEnd: '4px',
            },
            [`.${buttonClasses.endIcon}`]: {
                marginInlineStart: '4px',
                marginInlineEnd: 0,
            },
            [`&.${buttonClasses.small}`]: getSizeCSS('small'),
            [`&.${buttonClasses.medium}`]: getSizeCSS('medium'),
            [`&.${buttonClasses.large}`]: getSizeCSS('large'),
            [`&.${buttonClasses.extraLarge}`]: getSizeCSS('extraLarge'),
            [`&.${buttonClasses.primary}`]: getTypeCSS('primary'),
            [`&.${buttonClasses.secondary}`]: getTypeCSS('secondary'),
            [`&.${buttonClasses.tertiary}`]: getTypeCSS('tertiary'),
            [`&.${buttonClasses.danger}`]: {
                ...getDangerCSS(),
                [`&.${buttonClasses.primary}`]: getDangerCSS('primary'),
                [`&.${buttonClasses.secondary}`]: getDangerCSS('secondary'),
                [`&.${buttonClasses.tertiary}`]: getDangerCSS('tertiary'),
            },
            [`&.${buttonClasses.loading}`]: {
                cursor: 'default',
                ...getLoadingCSS(),
                [`&.${buttonClasses.primary}`]: getLoadingCSS('primary'),
                [`&.${buttonClasses.secondary}`]: getLoadingCSS('secondary'),
                [`&.${buttonClasses.tertiary}`]: getLoadingCSS('tertiary'),
            },
            [`&.${buttonClasses.disabled}, &.${buttonClasses.loading}.${buttonClasses.disabled}`]: {
                cursor: 'not-allowed',
                pointerEvents: 'auto',
                ...getDisabledCSS(),
                [`&.${buttonClasses.primary}`]: getDisabledCSS('primary'),
                [`&.${buttonClasses.secondary}`]: getDisabledCSS('secondary'),
                [`&.${buttonClasses.tertiary}`]: getDisabledCSS('tertiary'),
            },
            [`.${buttonClasses.loadingMask}`]: {
                background: tk(token.loadingMask),
                position: 'absolute',
                left: tk(token.loadingMaskOffset.default),
                right: tk(token.loadingMaskOffset.default),
                top: tk(token.loadingMaskOffset.default),
                bottom: tk(token.loadingMaskOffset.default),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: tk(token.borderRadius.default),
                [`.${buttonClasses.loadingIcon}`]: {
                    color: tk(token.loadingColor),
                    svg: {
                        fontSize: 16,
                    },
                },
            },
            ...getZoomIconDefaultStyles(),
        },
    };
    return newRootStyle;
}
export function generateClassnames(props) {
    const nameList = [];
    nameList.push(buttonClasses.root);
    if (props.type) {
        nameList.push(buttonClasses[props.type]);
    }
    if (props.size &&
        ['extraSmall', 'small', 'medium', 'large', 'extraLarge'].includes(props.size)) {
        if (props.size === 'extraSmall') {
            if (props.variant === 'icon') {
                nameList.push(buttonClasses[props.size]);
            }
        }
        else {
            nameList.push(buttonClasses[props.size]);
        }
    }
    if (props.danger) {
        nameList.push(buttonClasses.danger);
    }
    if (props.loading) {
        nameList.push(buttonClasses.loading);
    }
    if (props.disabled) {
        nameList.push(buttonClasses.disabled);
    }
    return clsx(nameList);
}
